import React from 'react';
import classnames from 'classnames';
import LayoutItems from './LayoutItems';
import { getBackgroundColorClassName, getGridSeperatorSizeClassName } from './getLayoutClassnames';

import type { LayoutProps, SevenFourContainerComposition, SixSixContainerComposition } from './LayoutTypes';

const LayoutContainer: React.FC<LayoutProps> = ({
    layoutClassNames,
    itemClassNames,
    backgroundColor,
    children,
    gridSeperatorSize,
    whiteSpaceNone = false,
}) => (
    <div
        className={classnames(
            'sgw-layout',
            layoutClassNames,
            getBackgroundColorClassName(backgroundColor),
            getGridSeperatorSizeClassName(gridSeperatorSize),
            { 'sgw-layout-white-space-none': whiteSpaceNone },
        )}
    >
        <LayoutItems itemClassNames={itemClassNames}>{children}</LayoutItems>
    </div>
);

export const LargeCenteredContainer: React.FC<LayoutProps> = ({
    layoutClassNames,
    itemClassNames,
    backgroundColor,
    gridSeperatorSize,
    whiteSpaceNone = false,
    children,
}) => (
    <LayoutContainer
        layoutClassNames={classnames('large-centered-container', layoutClassNames)}
        itemClassNames={itemClassNames}
        backgroundColor={backgroundColor}
        gridSeperatorSize={gridSeperatorSize}
        whiteSpaceNone={whiteSpaceNone}
    >
        {children}
    </LayoutContainer>
);

export const MediumCenteredContainer: React.FC<LayoutProps> = ({
    layoutClassNames,
    itemClassNames,
    backgroundColor,
    gridSeperatorSize,
    whiteSpaceNone = false,
    children,
}) => (
    <LayoutContainer
        layoutClassNames={classnames('medium-centered-container', layoutClassNames)}
        itemClassNames={itemClassNames}
        backgroundColor={backgroundColor}
        gridSeperatorSize={gridSeperatorSize}
        whiteSpaceNone={whiteSpaceNone}
    >
        {children}
    </LayoutContainer>
);

export const FormContainer: React.FC<LayoutProps> = ({
    layoutClassNames,
    itemClassNames,
    backgroundColor,
    gridSeperatorSize,
    whiteSpaceNone = false,
    children,
}) => (
    <LayoutContainer
        layoutClassNames={classnames('form-container', layoutClassNames)}
        itemClassNames={itemClassNames}
        backgroundColor={backgroundColor}
        gridSeperatorSize={gridSeperatorSize}
        whiteSpaceNone={whiteSpaceNone}
    >
        {children}
    </LayoutContainer>
);

export const SevenFourContainer: React.FC<Omit<LayoutProps, 'layoutClassNames' | 'itemClassNames'>> &
    SevenFourContainerComposition = ({ children, whiteSpaceNone = false, backgroundColor, gridSeperatorSize }) =>
    typeof children !== 'undefined' ? (
        <div
            className={classnames(
                'sgw-layout',
                'seven-four-container',
                { 'sgw-layout-white-space-none': whiteSpaceNone },
                getBackgroundColorClassName(backgroundColor),
                getGridSeperatorSizeClassName(gridSeperatorSize),
            )}
        >
            {children}
        </div>
    ) : null;

const Seven: React.FC<LayoutProps> = ({
    layoutClassNames,
    itemClassNames,
    backgroundColor,
    children,
    gridSeperatorSize,
    whiteSpaceNone = false,
}) =>
    typeof children !== 'undefined' ? (
        <div
            className={classnames(
                'sgw-layout-item seven',
                layoutClassNames,
                getBackgroundColorClassName(backgroundColor),
                getGridSeperatorSizeClassName(gridSeperatorSize),
                { 'sgw-layout-item-white-space-none': whiteSpaceNone },
            )}
        >
            <LayoutItems itemClassNames={itemClassNames}>{children}</LayoutItems>
        </div>
    ) : null;

const Four: React.FC<LayoutProps> = ({
    layoutClassNames,
    itemClassNames,
    backgroundColor,
    children,
    gridSeperatorSize,
    whiteSpaceNone = false,
}) =>
    typeof children !== 'undefined' ? (
        <div
            className={classnames(
                'sgw-layout-item four',
                layoutClassNames,
                getBackgroundColorClassName(backgroundColor),
                getGridSeperatorSizeClassName(gridSeperatorSize),
                { 'sgw-layout-item-white-space-none': whiteSpaceNone },
            )}
        >
            <LayoutItems itemClassNames={itemClassNames}>{children}</LayoutItems>
        </div>
    ) : null;

SevenFourContainer.Seven = Seven;
SevenFourContainer.Four = Four;

export const SixSixContainer: React.FC<React.PropsWithChildren> & SixSixContainerComposition = ({ children }) => (
    <div className="sgw-layout six-six-container">{children}</div>
);

const SixOne: React.FC<LayoutProps> = ({
    layoutClassNames,
    itemClassNames,
    backgroundColor,
    children,
    gridSeperatorSize,
    whiteSpaceNone = false,
}) =>
    typeof children !== 'undefined' ? (
        <div
            className={classnames(
                'sgw-layout-item six-one',
                layoutClassNames,
                getBackgroundColorClassName(backgroundColor),
                getGridSeperatorSizeClassName(gridSeperatorSize),
                { 'sgw-layout-item-white-space-none': whiteSpaceNone },
            )}
        >
            <LayoutItems itemClassNames={itemClassNames}>{children}</LayoutItems>
        </div>
    ) : null;

const SixTwo: React.FC<LayoutProps> = ({
    layoutClassNames,
    itemClassNames,
    backgroundColor,
    children,
    gridSeperatorSize,
    whiteSpaceNone = false,
}) =>
    typeof children !== 'undefined' ? (
        <div
            className={classnames(
                'sgw-layout-item six-two',
                layoutClassNames,
                getBackgroundColorClassName(backgroundColor),
                getGridSeperatorSizeClassName(gridSeperatorSize),
                { 'sgw-layout-item-white-space-none': whiteSpaceNone },
            )}
        >
            <LayoutItems itemClassNames={itemClassNames}>{children}</LayoutItems>
        </div>
    ) : null;

SixSixContainer.SixOne = SixOne;
SixSixContainer.SixTwo = SixTwo;

export default LayoutContainer;
